/**
 * PortraitWall — photo-first investigation board for the Connections
 * Explorer. The focus entity is pinned as the case card; every documented
 * connection hangs as a photo pinned to the wall: portrait, name, role,
 * relationship line, confidence and the shared investigations behind it.
 * Selecting a photo reveals the documented evidence (EvidencePanel) and
 * lets the investigator re-focus the wall without ever leaving the board.
 */
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Pin } from "lucide-react";
import { Drawer, DrawerContent, DrawerTitle } from "../ui/drawer";
import { EvidencePanel } from "./EvidencePanel";
import { KIND_META, groupOf, pathForEntity, portraitSrc } from "./explorerApi";
import { useRecordMedia } from "../media/PublishedMedia";
import { EntityImageFallback } from "../EntityImageFallback";

const API = process.env.REACT_APP_BACKEND_URL;
const full = (u) => (u && u.startsWith("/") ? `${API}${u}` : u);

/* Deterministic slight tilt so the wall feels hand-pinned, never random on re-render. */
const tilt = (i) => [(-1.6), 1.2, -0.8, 1.8, -1.2, 0.6][i % 6];

const Portrait = ({ node, className }) => {
  const g = groupOf(node);
  const media = useRecordMedia("any", node.slug);
  const rec = (media || []).find(
    (x) => !["internal_only", "background_reference"].includes(x.role));
  const img = node.portrait_url
    ? portraitSrc(node.portrait_url)
    : (rec ? full(rec.urls.thumb_webp || rec.urls.webp) : null);
  if (!img) {
    return <EntityImageFallback kind={node.kind || g} className={className} iconSize={20} />;
  }
  return (
    <img src={img} alt={node.name} loading="lazy" decoding="async"
         className={`object-cover object-top bg-black ${className}`}
         style={{ filter: "sepia(0.2) contrast(1.02)" }} />
  );
};

const WallCard = ({ node, edge, i, onSelect, selected }) => {
  const g = groupOf(node);
  const shared = (edge.shared_dossiers || []).slice(0, 2);
  return (
    <button type="button" onClick={onSelect}
            data-testid={`wall-card-${node.slug}`}
            className="relative text-left bg-[#151310] border transition-transform duration-200 hover:z-20 hover:scale-[1.03] focus:outline-none"
            style={{
              borderColor: selected ? "#D92D20" : "#2D2A26",
              transform: `rotate(${tilt(i)}deg)`,
              boxShadow: "0 10px 26px rgba(0,0,0,0.55)",
            }}>
      {/* Pin */}
      <span aria-hidden="true"
            className="absolute -top-2 left-1/2 -translate-x-1/2 z-10 w-3.5 h-3.5 rounded-full border border-black/60"
            style={{ background: selected ? "#D92D20" : "#8a2019",
                     boxShadow: "0 2px 4px rgba(0,0,0,0.7)" }} />
      <div className="p-2 pb-0">
        <Portrait node={node} className="w-full h-[130px] sm:h-[150px] border border-[#221F1C]" />
      </div>
      <div className="px-2.5 py-2">
        <div className="text-[12.5px] text-[#E4DFD3] font-heading leading-tight line-clamp-2">
          {node.name}
        </div>
        <div className="font-mono text-[8px] uppercase tracking-[0.2em] mt-0.5"
             style={{ color: KIND_META[g]?.color }}>
          {KIND_META[g]?.label}
        </div>
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <span className="font-mono text-[8px] uppercase tracking-[0.12em]"
                style={{ color: edge.strength === "corroborated" ? "#3FB950" : "#A68F5E" }}>
            {edge.strength}
          </span>
          <span className="font-mono text-[8px] text-[#5A554C]">
            {edge.co_count} shared file{edge.co_count === 1 ? "" : "s"}
          </span>
        </div>
        {shared.length > 0 && (
          <div className="mt-1.5 border-t border-[#221F1C] pt-1.5 space-y-0.5">
            {shared.map((d) => (
              <div key={d.slug} className="flex items-start gap-1 font-mono text-[8px] text-[#757065] leading-tight">
                <FileText size={7} className="mt-[1px] shrink-0 text-[#A68F5E]" />
                <span className="line-clamp-1">{d.title}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </button>
  );
};

const FocusPin = ({ node, dossiers }) => {
  const g = groupOf(node);
  return (
    <div className="relative border-2 bg-[#151310] p-3 sm:p-4 flex flex-col sm:flex-row gap-4"
         style={{ borderColor: "#D92D20", boxShadow: "0 14px 34px rgba(0,0,0,0.6)" }}
         data-testid={`wall-focus-${node.slug}`}>
      <span aria-hidden="true"
            className="absolute -top-2.5 left-6 z-10 inline-flex items-center gap-1 bg-[#D92D20] text-black font-mono text-[8px] uppercase tracking-[0.25em] px-2 py-0.5">
        <Pin size={8} /> Under investigation
      </span>
      <Portrait node={node} className="w-[120px] h-[150px] shrink-0 border border-[#221F1C]" />
      <div className="min-w-0">
        <div className="font-mono text-[8px] uppercase tracking-[0.3em]"
             style={{ color: KIND_META[g]?.color }}>
          {KIND_META[g]?.label}
        </div>
        <h2 className="font-heading text-2xl sm:text-3xl text-[#E4DFD3] leading-tight mt-0.5">
          {node.name}
        </h2>
        {(node.context_summary || node.summary) && (
          <p className="text-[11.5px] text-[#A39E93] leading-relaxed mt-1.5 line-clamp-3 max-w-xl">
            {node.context_summary || node.summary}
          </p>
        )}
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[9px] text-[#757065]">
          <span>{node.cameo_count} dossier appearance{node.cameo_count === 1 ? "" : "s"}</span>
          {node.has_profile && (
            <Link to={pathForEntity(node)}
                  className="text-[#A68F5E] hover:text-[#D92D20] underline underline-offset-2"
                  data-testid={`wall-profile-${node.slug}`}>
              full archive profile →
            </Link>
          )}
        </div>
        {dossiers.length > 0 && (
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {dossiers.slice(0, 4).map((d) => (
              <Link key={d.slug} to={`/topic/${d.slug}`}
                    className="inline-flex items-center gap-1 border border-[#2D2A26] px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-[0.12em] text-[#A39E93] hover:border-[#A68F5E]/60 hover:text-[#E4DFD3] transition-colors">
                <FileText size={8} className="text-[#A68F5E]" /> {d.title}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const PortraitWall = ({ graph, onFocus }) => {
  const [selected, setSelected] = useState(null); // slug of the other entity
  const focusNode = graph.nodes.find((n) => n.slug === graph.focus);

  const connections = useMemo(() => {
    if (!focusNode) return [];
    return graph.edges
      .filter((e) => e.a === focusNode.slug || e.b === focusNode.slug)
      .map((e) => {
        const other = graph.nodes.find(
          (n) => n.slug === (e.a === focusNode.slug ? e.b : e.a));
        return other ? { node: other, edge: e } : null;
      })
      .filter(Boolean)
      .sort((x, y) => y.edge.co_count - x.edge.co_count);
  }, [graph, focusNode]);

  if (!focusNode) return null;

  return (
    <div data-testid="portrait-wall"
         className="relative border border-[#2D2A26] p-4 sm:p-6"
         style={{
           background:
             "radial-gradient(ellipse at 20% 0%, rgba(166,143,94,0.05), transparent 55%)," +
             "radial-gradient(ellipse at 85% 100%, rgba(217,45,32,0.04), transparent 50%)," +
             "repeating-linear-gradient(0deg, transparent 0 3px, rgba(255,255,255,0.006) 3px 4px)," +
             "#0B0A09",
         }}>
      <FocusPin node={focusNode} dossiers={graph.dossiers || []} />

      <div className="font-mono text-[8px] uppercase tracking-[0.3em] text-[#757065] mt-7 mb-4">
        Pinned to the board · {connections.length} documented connection{connections.length === 1 ? "" : "s"}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-5"
           data-testid="wall-grid">
        {connections.map(({ node, edge }, i) => (
          <WallCard key={node.slug} node={node} edge={edge} i={i}
                    selected={selected === node.slug}
                    onSelect={() => setSelected(node.slug)} />
        ))}
      </div>
      {connections.length === 0 && (
        <div className="font-mono text-[10px] text-[#757065] p-4 border border-dashed border-[#2D2A26]">
          No co-appearing entities on record yet.
        </div>
      )}

      {/* Evidence behind the selected pin — follow moves the wall itself */}
      <Drawer open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DrawerContent className="bg-[#12110e] border-[#3A3630] max-h-[85vh] overflow-y-auto"
                       data-testid="wall-evidence-drawer">
          <DrawerTitle className="sr-only">Connection evidence</DrawerTitle>
          {selected && (
            <EvidencePanel a={focusNode.slug} b={selected}
                           onFollow={(s) => { setSelected(null); onFocus(s); }} />
          )}
        </DrawerContent>
      </Drawer>
    </div>
  );
};

export default PortraitWall;
